import { t } from '@lingui/core/macro'
import { AvailabilityMeetingPlace } from '@/core/graphql/types'
import { isFranceMode } from '@/utils/france-mode'
import { BookingState, BookingStepEnum, ConsultationMode } from '../model/bookingTypes'

function isSecondOpinion(meetingPlace: string | null) {
  return meetingPlace === AvailabilityMeetingPlace.SecondOpinion
}

export function shouldShowCompanionStep(language?: string | null) {
  return language?.split('-')[0]?.toLowerCase() === 'fr'
}

/**
 * createCustomizedStepOrder - Generates the sequence of steps for the booking flow
 * based on the current state (consultation mode, meeting place, etc.)
 */
export function createCustomizedStepOrder(state: BookingState, language?: string | null): BookingStepEnum[] {
  const is2ndOpinion = isSecondOpinion(state.meetingPlace)
  const isImmediate = state.consultationMode === ConsultationMode.Immediate
  // The consent wall is teleconsultation consent (informed-about-teleconsultation, etc.), which
  // doesn't apply to an in-person home visit.
  const isAtHome = state.meetingPlace === AvailabilityMeetingPlace.AtHome
  // France-only legal/regulatory steps — territoriality (LOCATION), companion confidentiality,
  // and the French consent wall (CONSENT) — are hidden for customers outside France.
  const franceMode = isFranceMode()
  const showCompanionStep = franceMode && shouldShowCompanionStep(language)

  return [
    BookingStepEnum.SPECIALITIES,
    BookingStepEnum.CONSULTATION_MODE,
    ...(isImmediate && franceMode ? [BookingStepEnum.LOCATION] : []),
    // Meeting Place and Availabilities only for scheduled (non-immediate)
    ...(!isImmediate ? [BookingStepEnum.MEETING_PLACE] : []),
    // Availabilities are only for scheduled non-second opinions
    ...(!is2ndOpinion && !isImmediate ? [BookingStepEnum.AVAILABILITIES] : []),
    ...(showCompanionStep ? [BookingStepEnum.COMPANION] : []),
    ...(franceMode && !isAtHome ? [BookingStepEnum.CONSENT] : []),
    BookingStepEnum.PATIENT_INFORMATION,
    ...(state.hasFormTemplate ? [BookingStepEnum.FORM_SUBMISSION] : []),
    ...(is2ndOpinion ? [BookingStepEnum.EXPERTISE_INFORMATION] : []),
    ...(state.requiresPayment && state.orderId && !state.paymentCompleted ? [BookingStepEnum.PAYMENT] : []),
    BookingStepEnum.CONFIRMATION,
  ]
}

// Function to determine next step based on current step
export function determineNextStep(state: BookingState, language?: string | null): BookingStepEnum | null {
  const currentStepOrder = createCustomizedStepOrder(state, language)
  const currentStepIndex = currentStepOrder.indexOf(state.currentStep)

  if (currentStepIndex === -1 || currentStepIndex === currentStepOrder.length - 1) {
    return null
  }

  // Find the first incomplete step after the current one
  for (let i = currentStepIndex + 1; i < currentStepOrder.length; i++) {
    const nextStep = currentStepOrder[i]
    if (!isStepCompleted(nextStep, state) || nextStep === BookingStepEnum.CONFIRMATION) {
      return nextStep
    }
  }

  return currentStepOrder[currentStepOrder.length - 1]
}

// Function to determine previous step based on current step
export function determinePreviousStep(state: BookingState, language?: string | null): BookingStepEnum | null {
  const currentStepOrder = createCustomizedStepOrder(state, language)
  const currentStepIndex = currentStepOrder.indexOf(state.currentStep)

  if (currentStepIndex <= 0) {
    return null
  }

  return currentStepOrder[currentStepIndex - 1]
}

/**
 * isStepCompleted - Centralized logic for step validation
 */
export function isStepCompleted(step: BookingStepEnum, state: BookingState): boolean {
  const validators: Partial<Record<BookingStepEnum, (s: BookingState) => boolean>> = {
    [BookingStepEnum.SPECIALITIES]: (s) => !!s.selectedSpeciality,
    [BookingStepEnum.CONSULTATION_MODE]: (s) => !!s.consultationMode,
    [BookingStepEnum.LOCATION]: (s) => !!s.patientLocation && (!!s.patientLocation.postalCode || s.patientLocation.declined),
    [BookingStepEnum.MEETING_PLACE]: (s) => !!s.meetingPlace,
    [BookingStepEnum.AVAILABILITIES]: (s) => isSecondOpinion(s.meetingPlace) || !!s.startDate,
    [BookingStepEnum.COMPANION]: (s) => {
      if (s.companion === undefined) return false
      if (s.companion === null) return true
      return !!(
        s.companion.firstName &&
        s.companion.lastName &&
        s.companion.relationshipType &&
        (s.companion.email || s.companion.phone) &&
        s.companion.confidentialityAgreement
      )
    },
    [BookingStepEnum.CONSENT]: (s) =>
      !!(s.consents?.freeChoice && s.consents?.informedAboutTeleconsultation && s.consents?.acceptedPricing && s.consents?.acceptedAt),
    [BookingStepEnum.PATIENT_INFORMATION]: (s) => !!(s.patientInfo.firstName && s.patientInfo.lastName && s.patientInfo.email && s.orderId),
    [BookingStepEnum.FORM_SUBMISSION]: (s) => !!s.formSubmissionId,
    [BookingStepEnum.EXPERTISE_INFORMATION]: (s) => !!s.expertiseData.comment,
    [BookingStepEnum.PAYMENT]: (s) => !s.requiresPayment || s.paymentCompleted,
    [BookingStepEnum.CONFIRMATION]: (s) => s.currentStep === BookingStepEnum.CONFIRMATION,
  }

  return validators[step]?.(state) ?? false
}

// Function to check if a step is accessible
export function isStepAccessible(step: BookingStepEnum, state: BookingState, language?: string | null): boolean {
  // First step is always accessible
  if (step === BookingStepEnum.SPECIALITIES) {
    return true
  }

  // Get the customized step order
  const customizedStepOrder = createCustomizedStepOrder(state, language)

  // Check if the step is in our customized order
  const stepIndex = customizedStepOrder.indexOf(step)
  if (stepIndex === -1) {
    return false
  }

  // Check if all previous steps are completed
  for (let i = 0; i < stepIndex; i++) {
    const prevStep = customizedStepOrder[i]
    if (!isStepCompleted(prevStep, state)) {
      return false
    }
  }

  return true
}

// Define metadata for each step
export function getStepMetadata() {
  return [
    { key: BookingStepEnum.SPECIALITIES, label: t`Speciality selection`, icon: '🔍' },
    { key: BookingStepEnum.CONSULTATION_MODE, label: t`Consultation mode`, icon: '⏱️' },
    { key: BookingStepEnum.LOCATION, label: t`Your location`, icon: '📍' },
    { key: BookingStepEnum.MEETING_PLACE, label: t`Meeting place`, icon: '🏢' },
    { key: BookingStepEnum.AVAILABILITIES, label: t`Meeting time`, icon: '📅' },
    { key: BookingStepEnum.COMPANION, label: t`Companion`, icon: '👥' },
    { key: BookingStepEnum.CONSENT, label: t`Consents`, icon: '✓' },
    { key: BookingStepEnum.PATIENT_INFORMATION, label: t`Patient information`, icon: '👤' },
    { key: BookingStepEnum.FORM_SUBMISSION, label: t`Fill out the form`, icon: '📝' },
    { key: BookingStepEnum.EXPERTISE_INFORMATION, label: t`Medical Information`, icon: '📋' },
    { key: BookingStepEnum.PAYMENT, label: t`Payment`, icon: '💳' },
    { key: BookingStepEnum.CONFIRMATION, label: t`Confirmation`, icon: '✅' },
  ]
}

// Function to get visible steps with their metadata
export function getVisibleSteps(state: BookingState, language?: string | null): Array<{ key: BookingStepEnum; label: string; icon: string }> {
  // Get all steps with metadata
  const allStepsWithMetadata = getStepMetadata()

  // Get the customized step order
  const visibleStepKeys = createCustomizedStepOrder(state, language)

  // Filter and return only the visible steps with their metadata
  return allStepsWithMetadata.filter((step) => visibleStepKeys.includes(step.key))
}

// Function to get URL for a step
export function getStepUrl(step: BookingStepEnum, meetingLinkSlug: string): string {
  // Construct the URL for the given step
  return `/meeting-links/${meetingLinkSlug}/booking/${step}`
}
